/**
 * IONLOOP Assistant — deterministic answer composer (Phase 4).
 *
 * Converts ranked retrieval results into a concise, human-readable, grounded
 * structured answer. All factual content is copied verbatim from the indexed
 * IONLOOP sources — nothing is invented, prices/limits/availability are never
 * fabricated, and each factual component keeps traceability to its source.
 */

var search = require('../knowledge/knowledge-search');
var sentenceSelector = require('./sentence-selector');
var templates = require('./answer-templates');
var actions = require('./answer-actions');
var sources = require('./source-builder');
var synonyms = require('../knowledge/synonym-map');

var QUALIFIER_PATTERN = /\b(planned|planning to|future|coming|where available|where it is available|under development|roadmap|aims to|will be|may be|not yet|for now|currently)\b/i;

var NOISE_INTRO_WORDS = [
    'read more', 'open', 'close', 'back', 'next', 'send', 'submit',
    'sign in', 'sign up', 'login', 'register', 'create account', 'learn more'
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
var _sectionLookup = null;

function buildSectionLookup(documents) {
    var lookup = {};
    (documents || []).forEach(function (doc) {
        (doc.sections || []).forEach(function (section) {
            lookup[section.id] = {
                documentId: doc.id,
                title: doc.title,
                url: doc.url,
                category: doc.category,
                description: doc.description,
                keywords: doc.keywords,
                section: section
            };
        });
    });
    return lookup;
}

function getLookup() {
    if (!_sectionLookup) {
        _sectionLookup = buildSectionLookup(search.getDocuments());
    }
    return _sectionLookup;
}

function hasQualifier(text) {
    return QUALIFIER_PATTERN.test(text || '');
}

function isNoiseLabel(label) {
    var lower = String(label || '').toLowerCase().trim();
    return NOISE_INTRO_WORDS.indexOf(lower) !== -1;
}

function capTitle(title) {
    if (!title) { return ''; }
    return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * Prefer a concise section heading that matches the query over a long
 * document title (e.g. "IONLOOP ID" vs "About Ionloop, IONLOOP ID & platform").
 */
function pickBestTitle(result, meaningfulTerms) {
    var heading = (result && result.heading) || '';
    var title = (result && result.title) || '';
    if (heading && heading.length <= 40 && meaningfulTerms.some(function (term) {
        return heading.toLowerCase().indexOf(term) !== -1;
    })) {
        return capTitle(heading);
    }
    return capTitle(title || heading);
}

// ---------------------------------------------------------------------------
// Intent → answer type
// ---------------------------------------------------------------------------
function mapIntentToType(intent) {
    switch (intent) {
        case 'definition': return 'definition';
        case 'how-to': return 'how-to';
        case 'navigation': return 'navigation';
        case 'capability': return 'capability';
        case 'comparison': return 'comparison';
        case 'account-help': return 'support';
        case 'support': return 'support';
        case 'overview': return 'overview';
        default: return 'definition';
    }
}

// ---------------------------------------------------------------------------
// Answer builders
// ---------------------------------------------------------------------------
function buildDefinitionAnswer(retrieval, top) {
    var best = top[0];
    var meaningful = retrieval.matchedQueryTerms || [];
    var title = pickBestTitle(best, meaningful);

    // Prefer a section whose heading contains a matched concept.
    var bestSection = best;
    var entityTokens = (title + ' ' + best.heading).toLowerCase();

    var sentences = sentenceSelector.selectSentences(bestSection.text, meaningful, bestSection.heading, {
        maxSentences: 4,
        minScore: 0.5
    });

    if (sentences.length === 0) {
        sentences = sentenceSelector.selectSentences(bestSection.text, meaningful, bestSection.heading, {
            maxSentences: 3,
            minScore: 0
        });
    }

    var intro = sentences[0] || bestSection.description || '';
    var paragraphs = sentences.slice(1, 2);
    var bullets = sentences.slice(2, 5);
    var bulletsText = bullets.length >= 2 ? bullets : [];

    var answer = templates.definition({
        title: title,
        intro: intro,
        paragraphs: paragraphs,
        bullets: bulletsText,
        confidence: retrieval.confidence,
        actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
        sources: sources.buildSources(top, { limit: 2 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: buildTrace(intro, paragraphs, bulletsText, bestSection.sectionId)
    });
    return answer;
}

function buildHowToAnswer(retrieval, top) {
    var meaningful = retrieval.matchedQueryTerms || [];
    var best = top[0];
    var title = pickBestTitle(best, meaningful);

    var steps = [];
    var paragraph = '';
    var foundSteps = false;

    // Prefer sections whose heading signals a workflow / how-to process.
    for (var i = 0; i < top.length && !foundSteps; i += 1) {
        var section = top[i];
        if (!/workflow|steps|how to|getting started|setup|process|guide|walkthrough/i.test(section.heading)) {
            continue;
        }
        var blocks = String(section.text || '').split('\n').map(function (b) { return b.trim(); }).filter(Boolean);
        var workflowSteps = blocks.filter(function (b) {
            return b.length >= 8 && b.length <= 180 && !isNoiseLabel(b);
        });
        if (workflowSteps.length >= 2) {
            steps = workflowSteps.slice(0, 6);
            foundSteps = true;
        }
    }

    // Fallback: numbered lists or short imperative lines anywhere.
    for (var i = 0; i < top.length && !foundSteps; i += 1) {
        var section = top[i];
        var blocks = String(section.text || '').split('\n').map(function (b) { return b.trim(); }).filter(Boolean);
        var numbered = blocks.filter(function (b) { return /^(\d+[.)]|\*)\s+/.test(b); });
        if (numbered.length >= 2) {
            steps = numbered.slice(0, 6);
            foundSteps = true;
        } else {
            var shortBlocks = blocks.filter(function (b) {
                return b.length >= 10 && b.length <= 160 &&
                    !isNoiseLabel(b) && !sentenceSelector.isNearDuplicate(b, paragraph);
            });
            if (shortBlocks.length >= 3) {
                steps = shortBlocks.slice(0, 6);
                foundSteps = true;
            }
        }
    }

    if (!foundSteps) {
        // Guarded: no step-by-step guide is available.
        var picked = sentenceSelector.selectSentences(best.text, meaningful, best.heading, {
            maxSentences: 2,
            minScore: 0.5
        });
        paragraph = picked[0] || '';
        var answer = templates.howTo({
            title: title,
            intro: 'I found information about ' + title + ', but the available IONLOOP content does not include a complete step-by-step setup guide.',
            paragraphs: paragraph ? [paragraph] : [],
            steps: [],
            confidence: retrieval.confidence,
            actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
            sources: sources.buildSources(top, { limit: 2 }),
            followUps: generateFollowUps(retrieval, top, meaningful),
            trace: buildTrace('', paragraph ? [paragraph] : [], [], best.sectionId)
        });
        return answer;
    }

    return templates.howTo({
        title: title,
        intro: 'Based on the available IONLOOP content:',
        steps: steps,
        confidence: retrieval.confidence,
        actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
        sources: sources.buildSources(top, { limit: 2 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: buildTrace('', [], steps, best.sectionId)
    });
}

function buildNavigationAnswer(retrieval, top) {
    var meaningful = retrieval.matchedQueryTerms || [];
    var best = top[0];
    var title = pickBestTitle(best, meaningful);
    var actionLinks = actions.selectActions(top, meaningful, { maxActions: 3 });

    var intro = 'You can find ' + (best.heading || title) + ' in the ' +
        (best.category || 'IONLOOP') + ' area of the platform.';

    var answer = templates.navigation({
        title: title,
        intro: intro,
        confidence: retrieval.confidence,
        actions: actionLinks,
        sources: sources.buildSources(top, { limit: 2 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: buildTrace(intro, [], [], best.sectionId)
    });
    return answer;
}

function buildCapabilityAnswer(retrieval, top) {
    var meaningful = retrieval.matchedQueryTerms || [];
    var best = top[0];

    // Determine the specific capability the user is asking about — typically
    // the last meaningful term (futures, spot, calendar, spreadsheets...).
    var capabilityTerm = findCapabilityTerm(retrieval, meaningful, best);

    var confirmed = false;
    var confirmSentence = '';
    var confirmSectionId = '';
    var confirmResult = null;
    var queryCategorySignals = retrieval.categorySignals || [];

    // Capability may be expressed via an alias (e.g. "spreadsheets" → the
    // "Sheet Editor" product). Expand the term through the controlled alias map
    // so a known product confirms even when the literal word is absent.
    var capTerms = [capabilityTerm];
    var canonicals = synonyms.getReverseMap()[capabilityTerm] || [];
    canonicals.forEach(function (canonical) {
        capTerms.push(canonical);
        (synonyms.STATIC_ALIASES[canonical] || []).forEach(function (alias) {
            capTerms.push(alias);
        });
    });

    // Confirm only against strong fields (title/heading/keywords) of the
    // highest-ranked results, and only when the result category is aligned
    // with the query's category signals (avoids confirming via incidental
    // mentions in unrelated articles).
    var confirmWindow = top.slice(0, 4);
    for (var i = 0; i < confirmWindow.length && !confirmed; i += 1) {
        var section = confirmWindow[i];
        var strongFields = (section.title + ' ' + section.heading + ' ' +
            (section.keywords || []).join(' ')).toLowerCase();
        var termHit = capTerms.some(function (term) {
            return term && (' ' + strongFields + ' ').indexOf(' ' + term + ' ') !== -1;
        });
        if (termHit) {
            if (queryCategorySignals.length === 0 || queryCategorySignals.indexOf(section.category) !== -1) {
                confirmed = true;
                confirmSectionId = section.sectionId;
                confirmResult = section;
                var sentences = sentenceSelector.selectSentences(section.text, meaningful, section.heading, {
                    maxSentences: 2,
                    minScore: 0.5
                });
                confirmSentence = sentences[0] || '';
            }
        }
    }

    var answer;
    var title = pickBestTitle(confirmResult || best, meaningful);
    if (confirmed) {
        var qualifierNote = hasQualifier(confirmSentence)
            ? ' Note that the source wording qualifies this (' + confirmSentence.slice(0, 80) + '...).'
            : '';
        var paragraphs = confirmSentence ? [confirmSentence] : [];
        var intro = 'Yes. The indexed IONLOOP content describes ' + (capabilityTerm || 'that feature') + '.';
        answer = templates.capability({
            title: title,
            intro: intro,
            paragraphs: paragraphs,
            bullets: [],
            confidence: Math.min(0.95, retrieval.confidence + 0.05),
            actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
            sources: sources.buildSources(top, { limit: 2 }),
            followUps: generateFollowUps(retrieval, top, meaningful),
            trace: buildTrace(intro, paragraphs, [], confirmSectionId)
        });
        if (qualifierNote) {
            answer.paragraphs.push(qualifierNote);
        }
    } else {
        var intro = 'The indexed IONLOOP content describes ' + (best.heading || title) +
            ', but I could not confirm ' + (capabilityTerm || 'that specific feature') +
            ' from the available source.';
        answer = templates.capability({
            title: title,
            intro: intro,
            paragraphs: [],
            bullets: [],
            confidence: Math.min(0.6, retrieval.confidence - 0.1),
            actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
            sources: sources.buildSources(top, { limit: 2 }),
            followUps: generateFollowUps(retrieval, top, meaningful),
            trace: buildTrace(intro, [], [], best.sectionId)
        });
    }
    return answer;
}

function findCapabilityTerm(retrieval, meaningful, best) {
    var stopwords = ['ionloop', 'has', 'have', 'support', 'supports', 'use', 'available', 'there', 'do', 'does', 'can', 'i', 'my', 'me'];
    var candidates = (meaningful || []).filter(function (t) { return stopwords.indexOf(t) === -1; });
    if (!candidates.length) {
        return (best.heading || '').toLowerCase().split(' ')[0] || '';
    }
    // Pick the most specific term (highest IDF = rarest in the corpus) so a
    // question like "does IONLOOP support options trading" targets "options"
    // and not the common word "trading".
    var stats = search.getStats();
    candidates.sort(function (a, b) { return stats.idf(a) - stats.idf(b); });
    return candidates[candidates.length - 1];
}

function buildOverviewAnswer(retrieval, top) {
    var meaningful = retrieval.matchedQueryTerms || [];
    // Diverse categories from the top results.
    var seenCategories = {};
    var bullets = [];
    var trace = [];

    (top || []).forEach(function (result) {
        var cat = result.category;
        if (seenCategories[cat]) {
            return;
        }
        seenCategories[cat] = true;
        var label = cat.replace(/-/g, ' ');
        bullets.push(capTitle(label) + ' — ' + capTitle(result.title || result.heading || ''));
        trace.push({ text: label + ' — ' + result.title, sourceIds: [result.sectionId] });
    });

    if (bullets.length > 6) {
        bullets = bullets.slice(0, 6);
    }

    var answer = templates.overview({
        title: 'IONLOOP',
        intro: 'IONLOOP brings several areas into one workspace:',
        bullets: bullets,
        confidence: retrieval.confidence,
        actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
        sources: sources.buildSources(top, { limit: 3 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: trace
    });
    return answer;
}

function buildComparisonAnswer(retrieval, top) {
    var entities = retrieval.comparisonEntities || [];
    var meaningful = retrieval.matchedQueryTerms || [];
    var best = top[0];
    var parts = [];
    var trace = [];
    var used = {};

    entities.forEach(function (entity) {
        var entityTerm = entity.toLowerCase().split(' ')[0];
        var found = null;
        for (var i = 0; i < top.length; i += 1) {
            var section = top[i];
            var haystack = (section.title + ' ' + section.heading + ' ' + section.text).toLowerCase();
            if (haystack.indexOf(entityTerm) !== -1 || haystack.indexOf(entity.toLowerCase()) !== -1) {
                found = section;
                break;
            }
        }
        if (found) {
            var sentences = sentenceSelector.selectSentences(found.text, meaningful.concat([entityTerm]), found.heading, {
                maxSentences: 2,
                minScore: 0.3
            });
            parts.push({ entity: capTitle(entity), points: sentences, sectionId: found.sectionId });
            sentences.forEach(function (s) { trace.push({ text: s, sourceIds: [found.sectionId] }); });
        } else {
            parts.push({ entity: capTitle(entity), points: [], sectionId: '' });
        }
    });

    var bullets = [];
    parts.forEach(function (part) {
        if (part.points.length) {
            bullets.push(part.entity + ': ' + part.points.join(' '));
        }
    });

    var intro = 'Here is what the indexed IONLOOP content says about ' +
        entities.map(capTitle).join(' and ') + '.';

    var answer = templates.comparison({
        title: entities.map(capTitle).join(' vs '),
        intro: intro,
        paragraphs: [],
        bullets: bullets,
        confidence: retrieval.confidence,
        actions: actions.selectActions(top, meaningful, { maxActions: 2 }),
        sources: sources.buildSources(top, { limit: 3 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: trace
    });
    return answer;
}

function buildSupportAnswer(retrieval, top) {
    var meaningful = retrieval.matchedQueryTerms || [];
    var best = top[0];
    var title = pickBestTitle(best, meaningful);
    var actionLinks = actions.selectActions(top, meaningful, { maxActions: 3 });

    var sentences = sentenceSelector.selectSentences(best.text, meaningful, best.heading, {
        maxSentences: 2,
        minScore: 0.3
    });
    var intro = sentences[0] || 'The available IONLOOP content covers this account topic.';

    var answer = templates.support({
        title: title,
        intro: intro,
        paragraphs: sentences.slice(1),
        confidence: retrieval.confidence,
        actions: actionLinks,
        sources: sources.buildSources(top, { limit: 3 }),
        followUps: generateFollowUps(retrieval, top, meaningful),
        trace: buildTrace(intro, sentences.slice(1), [], best.sectionId)
    });
    return answer;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
function buildTrace(intro, paragraphs, bullets, sourceId) {
    var trace = [];
    var add = function (text) {
        if (text && sourceId) {
            trace.push({ text: text, sourceIds: [sourceId] });
        }
    };
    if (intro) { add(intro); }
    (paragraphs || []).forEach(add);
    (bullets || []).forEach(add);
    return trace;
}

/**
 * Deterministic follow-up suggestions derived from related headings, linked
 * pages and categories of the top results.
 */
function generateFollowUps(retrieval, top, meaningful) {
    var lookup = getLookup();
    var followUps = [];
    var seen = {};

    (top || []).forEach(function (result) {
        var info = lookup[result.sectionId];
        if (!info) { return; }
        var section = info.section;

        // Related sibling headings within the same document.
        var siblingHeadings = info.document && info.document.sections
            ? info.document.sections.map(function (s) { return s.heading; })
            : [];
        siblingHeadings.forEach(function (heading) {
            if (!heading || heading === result.heading) { return; }
            var norm = heading.toLowerCase();
            if (norm.length < 3 || norm.length > 60) { return; }
            var q = 'Tell me more about ' + heading + '.';
            if (!seen[q]) {
                seen[q] = true;
                followUps.push(q);
            }
        });

        // Linked pages become "Tell me about X" prompts.
        (section.links || []).forEach(function (link) {
            if (isNoiseLabel(link.label) || /^javascript:/i.test(link.url)) { return; }
            var label = (link.label || '').trim();
            if (label.length < 3 || label.length > 50) { return; }
            var q = 'Tell me about ' + label + '.';
            if (!seen[q]) {
                seen[q] = true;
                followUps.push(q);
            }
        });
    });

    if (followUps.length < 2 && meaningful && meaningful.length) {
        var topic = meaningful[meaningful.length - 1];
        ['How does it work?', 'Is it secure?', 'What else can it do?'].forEach(function (q) {
            if (topic && !seen[q]) {
                seen[q] = true;
                followUps.push(q);
            }
        });
    }

    return followUps.slice(0, 4);
}

// ---------------------------------------------------------------------------
// Ambiguous / insufficient
// ---------------------------------------------------------------------------
function buildAmbiguousAnswer(retrieval) {
    var suggestions = retrieval.suggestions || [];
    var bullets = suggestions.map(function (s) { return s; });
    return templates.ambiguous({
        intro: 'I found several related topics. What would you like to know?',
        bullets: bullets,
        suggestions: suggestions,
        confidence: retrieval.confidence || 0.2,
        sources: []
    });
}

function buildInsufficientAnswer(retrieval, query) {
    var isCodeOrTech = /(code|script|program|api key for .* language)/i.test(query || '');
    var paragraphs = [];
    if (isCodeOrTech) {
        paragraphs.push('I can help with IONLOOP product topics such as IONLOOP ID, trading tools, planning, wallet, account features, automation and business operations.');
    } else {
        paragraphs.push('I can help with IONLOOP ID, trading tools, planning, wallet, account features, automation and business operations.');
    }
    return templates.insufficient({
        confidence: retrieval.confidence || 0.1,
        paragraphs: paragraphs,
        sources: []
    });
}

// ---------------------------------------------------------------------------
// Main composer API
// ---------------------------------------------------------------------------
function composeAnswer(question, retrieval, options) {
    options = options || {};
    var start = Date.now();

    if (!retrieval || !retrieval.results) {
        return templates.insufficient({
            confidence: 0,
            paragraphs: ['IONLOOP knowledge is temporarily unavailable.']
        });
    }

    var top = retrieval.results.slice(0, 6).map(function (result) {
        var info = getLookup()[result.sectionId];
        return {
            documentId: result.documentId,
            sectionId: result.sectionId,
            title: result.title,
            heading: result.heading,
            category: result.category,
            url: result.url,
            score: result.score,
            text: info ? info.section.text : '',
            links: info ? info.section.links : [],
            keywords: info ? info.section.keywords : [],
            description: info ? info.description : ''
        };
    });

    var answer;

    if (retrieval.status === 'ambiguous') {
        answer = buildAmbiguousAnswer(retrieval);
    } else if (retrieval.status === 'insufficient') {
        answer = buildInsufficientAnswer(retrieval, question);
    } else {
        var type = options.type || mapIntentToType(retrieval.intent);
        switch (type) {
            case 'how-to': answer = buildHowToAnswer(retrieval, top); break;
            case 'navigation': answer = buildNavigationAnswer(retrieval, top); break;
            case 'capability': answer = buildCapabilityAnswer(retrieval, top); break;
            case 'overview': answer = buildOverviewAnswer(retrieval, top); break;
            case 'comparison': answer = buildComparisonAnswer(retrieval, top); break;
            case 'support': answer = buildSupportAnswer(retrieval, top); break;
            default: answer = buildDefinitionAnswer(retrieval, top); break;
        }
        // Preserve the retrieval confidence when the composer built a valid answer.
        if (answer.confidence !== retrieval.confidence && answer.status === 'answered') {
            answer.confidence = retrieval.confidence;
        }
    }

    // Response refinement (Phase 5): shorter / more detail reuse.
    if (options.verbosity === 'short' && answer.paragraphs && answer.paragraphs.length > 1) {
        answer.paragraphs = answer.paragraphs.slice(0, 1);
    }
    if (options.verbosity === 'short' && answer.bullets && answer.bullets.length > 3) {
        answer.bullets = answer.bullets.slice(0, 3);
    }

    answer.exchangeId = options.exchangeId || null;
    answer.primaryEntity = options.primaryEntity || answer.title || '';
    answer.elapsedMs = Date.now() - start;
    return answer;
}

// Allow tests to reset the lookup after loading a different index.
function resetCache() {
    _sectionLookup = null;
}

module.exports = {
    composeAnswer: composeAnswer,
    resetCache: resetCache,
    buildSectionLookup: buildSectionLookup,
    mapIntentToType: mapIntentToType,
    hasQualifier: hasQualifier
};
